import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";

interface PriorityDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (description: string) => Promise<void>;
  initialDescription?: string;
  title: string;
}

export function PriorityDialog({
  open,
  onClose,
  onSubmit,
  initialDescription = "",
  title,
}: PriorityDialogProps) {
  const [description, setDescription] = useState(initialDescription);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const maxLen = 50;

  useEffect(() => {
    if (open) {
      setDescription(initialDescription);
      setError("");
    }
  }, [open, initialDescription]);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Omschrijving is verplicht");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(description.trim());
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Er is een fout opgetreden",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title}
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Omschrijving"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={!!error}
          helperText={error || `${description.trim().length}/${maxLen}`}
          disabled={isSubmitting}
          inputProps={{ maxLength: maxLen }}
          sx={{ mt: 1 }}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !isSubmitting) {
              handleSubmit();
            }
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Annuleren
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Opslaan..." : "Opslaan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
